import { FC, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useProfileReview } from "./useProfileReview";
import { MandatoryAttributesTable } from "./MandatoryAttributesTable";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { TextInput } from "@sinch/components/form/TextInput";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { UploadyProvider } from "@sinch/components/form/UploadInput/UploadyProvider";

type ProfileReview = ReturnType<typeof useProfileReview>;

interface ReviewProfileDialogContentProps {
  onApproved: () => void;
  onClose: () => void;
  onDeclined: () => void;
  workerId: string;
}

export const ReviewProfileDialogContent: FC<ReviewProfileDialogContentProps> = ({
  onApproved,
  onClose,
  onDeclined,
  workerId,
}) => {
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const { form, handleApprove, handleDecline, attributes, loading } = useProfileReview({
    workerId,
    onSuccess: onDeclined,
  });

  const submitApproval = form.handleSubmit(async (data) => {
    if (await handleApprove(data)) {
      onApproved();
    }
  });

  return (
    <FormProvider {...form}>
      <UploadyProvider onFinish={() => form.setReady(true)} onStart={() => form.setReady(false)}>
        {showDeclineForm ? (
          <DeclineView loading={loading} onBack={() => setShowDeclineForm(false)} onDecline={handleDecline} />
        ) : (
          <ApproveView
            attributes={attributes}
            loading={loading}
            onApprove={submitApproval}
            onCancel={onClose}
            onShowDeclineForm={() => setShowDeclineForm(true)}
          />
        )}
      </UploadyProvider>
    </FormProvider>
  );
};

interface ApproveViewProps {
  attributes: ProfileReview["attributes"];
  loading: boolean;
  onApprove: () => void;
  onCancel: () => void;
  onShowDeclineForm: () => void;
}

const ApproveView: FC<ApproveViewProps> = ({ attributes, loading, onApprove, onCancel, onShowDeclineForm }) => {
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);
  return (
    <>
      <DialogContent>
        <Stack gap={1}>
          <Typography sx={{ mb: 2 }}>{t("ProfileReview.approveDescription")}</Typography>
          {attributes !== undefined && attributes.length > 0 && (
            <MandatoryAttributesTable attributes={attributes} onEditingChange={setIsEditing} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-cy="profile-review-cancel-btn" onClick={onCancel} variant="text">
          {t("cancel")}
        </Button>
        <Button
          color="error"
          data-cy="decline-profile-btn"
          disabled={loading || isEditing}
          onClick={onShowDeclineForm}
          variant="outlined"
        >
          {t("ProfileReview.declineProfile")}
        </Button>
        <Button
          color="success"
          data-cy="approve-profile-btn"
          disabled={loading || isEditing}
          onClick={onApprove}
          variant="contained"
        >
          {t("ProfileReview.approveProfile")}
        </Button>
      </DialogActions>
    </>
  );
};

interface DeclineViewProps {
  loading: boolean;
  onBack: () => void;
  onDecline: ProfileReview["handleDecline"];
}

const DeclineView: FC<DeclineViewProps> = ({ loading, onBack, onDecline }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");

  return (
    <>
      <DialogContent>
        <Stack gap={1}>
          <Typography sx={{ mb: 2 }}>{t("ProfileReview.declineDescription")}</Typography>
          <TextInput
            autoFocus
            data-cy="decline-reason-input"
            fullWidth
            label={t("ProfileReview.declineReason")}
            minRows={3}
            multiline
            onChange={(e) => setMessage(e.target.value)}
            value={message}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-cy="decline-back-btn" onClick={onBack} variant="text">
          {t("Action.back")}
        </Button>
        <Button
          color="error"
          data-cy="confirm-decline-btn"
          disabled={message === "" || loading}
          onClick={() => onDecline(message)}
          variant="contained"
        >
          {t("ProfileReview.declineProfile")}
        </Button>
      </DialogActions>
    </>
  );
};
